import React, { useRef, useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';

const TabNavigation = ({ activeTab, onTabChange, tabCounts }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  const tabs = [
    {
      id: 'customers',
      label: 'Customers',
      icon: 'Users',
      count: tabCounts?.customers,
      badge: tabCounts?.activeCustomers,
      color: 'text-blue-600'
    },
    {
      id: 'quotes',
      label: 'Quotes',
      icon: 'FileText',
      count: tabCounts?.quotes,
      badge: tabCounts?.pendingQuotes,
      color: 'text-purple-600'
    },
    {
      id: 'retainer-invoices',
      label: 'Retainer Invoices',
      icon: 'Wallet',
      count: tabCounts?.retainerInvoices,
      badge: tabCounts?.unusedRetainers,
      color: 'text-green-600'
    },
    {
      id: 'invoices',
      label: 'Invoices',
      icon: 'Receipt',
      count: tabCounts?.invoices,
      badge: tabCounts?.overdueInvoices,
      color: 'text-orange-600'
    },
    {
      id: 'recurring-invoices',
      label: 'Recurring Invoices',
      icon: 'RotateCcw',
      count: tabCounts?.recurringInvoices,
      badge: tabCounts?.activeRecurring,
      color: 'text-indigo-600'
    },
    {
      id: 'delivery-challans',
      label: 'Delivery Challans',
      icon: 'Truck',
      count: tabCounts?.deliveryChallans,
      badge: tabCounts?.pendingDeliveries,
      color: 'text-cyan-600'
    },
    {
      id: 'payment-links',
      label: 'Payment Links',
      icon: 'Link',
      count: tabCounts?.paymentLinks,
      badge: tabCounts?.activePaymentLinks,
      color: 'text-pink-600'
    },
    {
      id: 'payments-received',
      label: 'Payment Received',
      icon: 'CreditCard',
      count: tabCounts?.paymentsReceived,
      badge: tabCounts?.pendingPayments,
      color: 'text-teal-600'
    },
    {
      id: 'credit-notes',
      label: 'Credit Notes',
      icon: 'FileCheck',
      count: tabCounts?.creditNotes,
      badge: tabCounts?.unusedCreditNotes || 0,
      color: 'text-red-600'
    }
  ];

  // Check scroll position and update scroll indicators
  const checkScrollPosition = () => {
    if (scrollContainerRef?.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef?.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  // Auto-scroll to active tab if it's not fully visible
  const scrollToActiveTab = () => {
    if (scrollContainerRef?.current && activeTab) {
      const activeTabElement = scrollContainerRef?.current?.querySelector(`[data-tab-id="${activeTab}"]`);
      if (activeTabElement) {
        const containerRect = scrollContainerRef?.current?.getBoundingClientRect();
        const tabRect = activeTabElement?.getBoundingClientRect();
        
        // Check if tab is partially or fully out of view
        if (tabRect?.right > containerRect?.right || tabRect?.left < containerRect?.left) {
          activeTabElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }
    }
  };

  // Scroll left/right functions
  const scrollLeft = () => {
    if (scrollContainerRef?.current) {
      scrollContainerRef?.current?.scrollBy({
        left: -200,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef?.current) {
      scrollContainerRef?.current?.scrollBy({
        left: 200,
        behavior: 'smooth'
      });
    }
  };

  // Set up scroll listeners and initial scroll position
  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (scrollContainer) {
      checkScrollPosition();
      scrollContainer?.addEventListener('scroll', checkScrollPosition);
      
      // Initial scroll to active tab
      setTimeout(scrollToActiveTab, 100);
      
      return () => {
        scrollContainer?.removeEventListener('scroll', checkScrollPosition);
      };
    }
  }, []);

  // Scroll to active tab when it changes
  useEffect(() => {
    scrollToActiveTab();
  }, [activeTab]);

  return (
    <div className="bg-card border border-border rounded-lg shadow-professional mb-6 relative">
      {/* Left scroll button */}
      {showLeftScroll && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-card to-transparent z-10 flex items-center justify-center hover:from-card/90 transition-colors"
          aria-label="Scroll left"
        >
          <Icon name="ChevronLeft" size={16} className="text-muted-foreground" />
        </button>
      )}

      {/* Right scroll button */}
      {showRightScroll && (
        <button
          onClick={scrollRight}
          className="absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-card to-transparent z-10 flex items-center justify-center hover:from-card/90 transition-colors"
          aria-label="Scroll right"
        >
          <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
        </button>
      )}

      <div 
        ref={scrollContainerRef}
        className="flex overflow-x-auto scrollbar-hide relative"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {tabs?.map((tab, index) => (
          <button
            key={tab?.id}
            data-tab-id={tab?.id}
            onClick={() => onTabChange(tab?.id)}
            className={`
              flex items-center gap-3 px-4 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 min-w-fit flex-shrink-0 relative
              ${activeTab === tab?.id
                ? `border-primary text-primary bg-primary/5` 
                : `border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30`
              }
              ${tab?.id === 'credit-notes' ? 'animate-pulse-subtle' : ''}
            `}
          >
            <Icon name={tab?.icon} size={18} className={activeTab === tab?.id ? 'text-primary' : tab?.color} />
            
            {/* Responsive label display */}
            <span className="hidden lg:inline">{tab?.label}</span>
            <span className="hidden md:inline lg:hidden">
              {tab?.label?.includes(' ') ? tab?.label?.split(' ')?.[0] : tab?.label}
            </span>
            <span className="md:hidden text-xs">
              {tab?.label?.includes(' ') ? 
                tab?.label?.split(' ')?.map(word => word?.[0])?.join('') : 
                tab?.label?.substring(0, 4)
              }
            </span>

            {/* Count badge */}
            {tab?.count !== undefined && tab?.count > 0 && (
              <span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full min-w-[24px] text-center">
                {tab?.count}
              </span>
            )}

            {/* Alert badge for pending/important items */}
            {tab?.badge > 0 && (
              <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full min-w-[24px] text-center animate-pulse">
                {tab?.badge}
              </span>
            )}

            {/* Special indicator for Credit Notes */}
            {tab?.id === 'credit-notes' && (
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            )}
          </button>
        ))}
      </div>
      
      {/* Mobile helper text */}
      <div className="md:hidden text-xs text-muted-foreground text-center py-2 border-t bg-muted/20">
        <div className="flex items-center justify-center gap-2">
          <Icon name="ArrowLeft" size={12} />
          <span>Scroll to see all {tabs?.length} tabs</span>
          <Icon name="ArrowRight" size={12} />
        </div>
      </div>
    </div>
  );
};

export default TabNavigation;